import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, getImageUrl } from '../../../config';
import { toastError, toastSuccess, errorMessage } from '../../../lib/toast';
import { REFUSALS } from '../../../lib/permissionErrors';
import { formatDateTime } from '../../../lib/formatDate';
import { useAuth } from '../../../context/AuthContext';
import SsmParamPicker from './SsmParamPicker';
import SecretRevealRow from './SecretRevealRow';
import CommentThread from '../../buildApp/CommentThread';
import StatusTracker from '../../buildApp/StatusTracker';
import { StatusPill } from './appStatus';
import AdminShell from '../shell/AdminShell';
import AdminAccessDenied from '../shell/AdminAccessDenied';
import useAdminAccess from '../../../context/useAdminAccess';

const TOOL_TYPE_LABEL = {
  tool: 'Tool',
  document: 'Document',
  sheet: 'Sheet',
  service: 'Service',
  dashboard: 'Dashboard',
};

const APPS_LIST_HASH = '/admin/apps';

// Matches SSO_TOOL_NAME in server/routes/apps.js — the mandatory, server-injected
// Google SSO row. Each app needs its OWN SSO secret, so it's flagged for admins.
const SSO_TOOL_NAME = 'Google SSO';
const SSO_TOOL_ICON = '/images/google-sso.png';

// Statuses where tool/collaborator edits are still permitted.
const PRE_PUBLISH = new Set(['pending', 'approved', 'provisioning', 'active', 'publish_requested']);

const TOOL_EDIT_ERRORS = {
  not_editable: 'This app can no longer be edited.',
  duplicate_tool: 'That tool is already on this app.',
  unknown_tool: 'That tool no longer exists.',
  tools_required: 'An app needs at least one tool.',
  sso_required: "Google SSO is required and can't be removed.",
  forbidden: "You don't have permission to do that.",
  bad_tool: 'Pick a catalog tool or enter a custom name.',
};

const COLLABORATOR_ERRORS = {
  already_collaborator: "They're already a collaborator.",
  is_creator: 'That user is the creator.',
  not_editable: 'This app can no longer be edited.',
};

export default function AppDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const adminEmail = (auth && auth.user && auth.user.email) || '';
  const { can } = useAdminAccess();

  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null); // { kind, text }

  // Review (pending) state.
  const [notes, setNotes] = useState('');
  const [deciding, setDeciding] = useState(false);

  // Provision state.
  const [assignTo, setAssignTo] = useState(null); // app_request_tool_id being assigned
  const [kind, setKind] = useState('ssm');

  // Publish-to-catalog state.
  const [categories, setCategories] = useState([]);
  const [pubType, setPubType] = useState('service');
  const [pubLink, setPubLink] = useState('');
  const [pubCategory, setPubCategory] = useState('');
  const [pubIcon, setPubIcon] = useState('');
  const [publishing, setPublishing] = useState(false);

  // Request-changes + comments state.
  const [changesComment, setChangesComment] = useState('');
  const [requestingChanges, setRequestingChanges] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentBody, setCommentBody] = useState('');
  const [posting, setPosting] = useState(false);

  // Per-app reveal audit history.
  const [reveals, setReveals] = useState([]);

  // Add-tool row state.
  const [addingTool, setAddingTool] = useState(false);
  const [addMode, setAddMode] = useState('catalog');
  const [catalog, setCatalog] = useState([]);
  const [selTool, setSelTool] = useState('');
  const [customName, setCustomName] = useState('');
  const [toolNote, setToolNote] = useState('');
  const [addingToolBusy, setAddingToolBusy] = useState(false);

  // Add-collaborator state.
  const [addingCollab, setAddingCollab] = useState(false);
  const [collabQuery, setCollabQuery] = useState('');
  const [collabResults, setCollabResults] = useState([]);
  const [collabSearching, setCollabSearching] = useState(false);

  const mountedRef = useRef(true);
  useEffect(() => { mountedRef.current = true; return () => { mountedRef.current = false; }; }, []);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/app_requests/${id}`, { withCredentials: true });
      if (mountedRef.current) {
        setDetail(res.data);
        setError(null);
        // Prefill the publish form from the requester's production details.
        if (res.data.production_url) setPubLink((prev) => prev || res.data.production_url);
        if (res.data.production_icon_url) setPubIcon((prev) => prev || res.data.production_icon_url);
      }
    } catch {
      if (mountedRef.current) setError('Failed to load app.');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [id]);

  const loadComments = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/app_requests/${id}/comments`, { withCredentials: true });
      if (mountedRef.current) setComments(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* non-fatal */
    }
  }, [id]);

  const loadReveals = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/admin/app_requests/${id}/reveals`, { withCredentials: true });
      if (mountedRef.current) setReveals(Array.isArray(res.data) ? res.data : []);
    } catch {
      /* non-fatal */
    }
  }, [id]);

  useEffect(() => {
    if (!can('apps.view')) return;
    load(); loadComments(); loadReveals();
  }, [load, loadComments, loadReveals, can]);

  // Debounced collaborator search.
  useEffect(() => {
    if (!addingCollab) return undefined;
    let cancelled = false;
    const run = async () => {
      setCollabSearching(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/users/search`, {
          params: { q: collabQuery.trim() }, withCredentials: true,
        });
        if (!cancelled && mountedRef.current) setCollabResults(Array.isArray(res.data) ? res.data : []);
      } catch {
        if (!cancelled && mountedRef.current) setCollabResults([]);
      } finally {
        if (!cancelled && mountedRef.current) setCollabSearching(false);
      }
    };
    const t = setTimeout(run, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [collabQuery, addingCollab]);

  const requestChanges = async () => {
    setMessage(null);
    if (!changesComment.trim()) { setMessage({ kind: 'error', text: 'Add a comment describing the changes needed.' }); return; }
    setRequestingChanges(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/app_requests/${id}/request-changes`,
        { comment: changesComment.trim() }, { withCredentials: true });
      toastSuccess('Changes requested. The app is back in draft and the requester has your feedback.');
      if (mountedRef.current) {
        setRequestingChanges(false);
        setChangesComment('');
        await load();
        await loadComments();
      }
    } catch (err) {
      toastError(errorMessage(err, "Couldn't request changes.", {
        not_publish_requested: 'This app is not awaiting publish review.',
        ...REFUSALS,
      }));
      if (mountedRef.current) setRequestingChanges(false);
    }
  };

  const postComment = async () => {
    if (!commentBody.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/api/app_requests/${id}/comments`,
        { body: commentBody.trim() }, { withCredentials: true });
      if (mountedRef.current) { setComments((prev) => [...prev, res.data]); setCommentBody(''); setPosting(false); }
    } catch (err) {
      toastError(errorMessage(err, "Couldn't post comment."));
      if (mountedRef.current) setPosting(false);
    }
  };

  // Approve / reject (pending → approved | rejected). Navigates back on success.
  const decide = async (decision) => {
    setMessage(null);
    setDeciding(true);
    try {
      await axios.patch(`${API_BASE_URL}/api/admin/app_requests/${id}`,
        { status: decision, admin_notes: notes || null }, { withCredentials: true });
      navigate(APPS_LIST_HASH); // safe post-unmount; no state reset after success
    } catch (err) {
      toastError(errorMessage(err, 'Action failed.', REFUSALS));
      if (mountedRef.current) setDeciding(false);
    }
  };

  // Assign / unassign a reference (provision). Reloads detail in place.
  const assign = async (artId, paramName) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/app_requests/${id}/secrets`,
        { app_request_tool_id: artId, assignment_kind: kind, ssm_param_name: paramName },
        { withCredentials: true });
      toastSuccess('Reference assigned.');
      if (mountedRef.current) {
        setAssignTo(null);
        await load();
      }
    } catch (err) {
      toastError(errorMessage(err, "Couldn't assign the reference. Try again.", REFUSALS));
    }
  };

  const unassign = async (secretId) => {
    try {
      await axios.delete(`${API_BASE_URL}/api/admin/app_requests/${id}/secrets/${secretId}`, { withCredentials: true });
      toastSuccess('Reference removed.');
      if (mountedRef.current) await load();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't remove the reference. Try again.", REFUSALS));
    }
  };

  const activate = async () => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/app_requests/${id}/activate`, {}, { withCredentials: true });
      navigate(APPS_LIST_HASH); // safe post-unmount; no state reset after success
    } catch (err) {
      toastError(errorMessage(err, "Couldn't activate the app. Try again.", REFUSALS));
    }
  };

  // Open the add-tool row and lazily fetch the catalog.
  const openAddTool = async () => {
    setAddingTool(true);
    if (catalog.length === 0) {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/admin/tools/catalog-for-apps`, { withCredentials: true });
        if (mountedRef.current) setCatalog(Array.isArray(res.data) ? res.data : []);
      } catch {
        /* catalog stays empty — user can still use custom mode */
      }
    }
  };

  const addTool = async () => {
    setAddingToolBusy(true);
    try {
      const body = addMode === 'catalog'
        ? { tool_id: selTool, usage_note: toolNote }
        : { custom_tool_name: customName, usage_note: toolNote };
      await axios.post(`${API_BASE_URL}/api/app_requests/${id}/tools`, body, { withCredentials: true });
      toastSuccess('Tool added.');
      setAddingTool(false);
      setAddMode('catalog');
      setCustomName('');
      setToolNote('');
      setSelTool('');
      if (mountedRef.current) await load();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't update the tools. Try again.", TOOL_EDIT_ERRORS));
    } finally {
      if (mountedRef.current) setAddingToolBusy(false);
    }
  };

  const removeTool = async (t) => {
    if ((t.secrets || []).length > 0 &&
        !window.confirm('This tool has an assigned secret. Removing it deletes that assignment. Continue?')) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/app_requests/${id}/tools/${t.app_request_tool_id}`, { withCredentials: true });
      toastSuccess('Tool removed.');
      if (mountedRef.current) await load();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't update the tools. Try again.", TOOL_EDIT_ERRORS));
    }
  };

  const addCollaborator = async (u) => {
    try {
      await axios.post(`${API_BASE_URL}/api/admin/app_requests/${id}/collaborators`,
        { user_id: u.id }, { withCredentials: true });
      toastSuccess('Collaborator added.');
      setAddingCollab(false);
      setCollabQuery('');
      setCollabResults([]);
      if (mountedRef.current) await load();
    } catch (err) {
      toastError(errorMessage(err, "Couldn't add the collaborator. Try again.", { ...COLLABORATOR_ERRORS, ...REFUSALS }));
      setAddingCollab(false);
      setCollabQuery('');
      setCollabResults([]);
    }
  };

  // Load categories for the publish form once the app is awaiting publish review.
  const needsPublishForm = detail && detail.status === 'publish_requested' && !detail.published_tool_id;
  const categoriesLoadedRef = useRef(false);
  useEffect(() => {
    if (!needsPublishForm || categoriesLoadedRef.current) return;
    categoriesLoadedRef.current = true;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/categories`, { withCredentials: true });
        if (mountedRef.current) setCategories(Array.isArray(res.data) ? res.data : []);
      } catch {
        /* non-fatal — category picker just stays empty */
      }
    })();
  }, [needsPublishForm]);

  const publish = async () => {
    setMessage(null);
    if (!pubLink.trim()) { setMessage({ kind: 'error', text: 'Link is required to publish.' }); return; }
    setPublishing(true);
    try {
      await axios.post(`${API_BASE_URL}/api/admin/app_requests/${id}/publish`,
        {
          type: pubType,
          link: pubLink.trim(),
          category_id: pubCategory ? Number(pubCategory) : null,
          icon_url: pubIcon.trim() || null,
        },
        { withCredentials: true });
      toastSuccess('App published to the catalog. It can now be added to departments via Tools / Departments.');
      if (mountedRef.current) {
        setPublishing(false);
        await load();
      }
    } catch (err) {
      const toolName = err?.response?.data?.tool_name;
      toastError(errorMessage(err, 'Publish failed.', {
        link_exists: `A tool with this link already exists${toolName ? `: "${toolName}"` : ''}.`,
        link_required: 'A link is required to publish (none provided and no production URL on file).',
        not_publish_requested: 'This app is not awaiting publish review.',
        already_published: 'This app is already published.',
        ...REFUSALS,
      }));
      if (mountedRef.current) setPublishing(false);
    }
  };

  if (!can('apps.view')) {
    return (
      <AdminShell activeKey="apps" title="App request">
        <AdminAccessDenied />
      </AdminShell>
    );
  }

  if (loading) {
    return (
      <AdminShell activeKey="apps" title="App request">
        <div className="apps-detail">
          <p className="at-muted">Loading…</p>
        </div>
      </AdminShell>
    );
  }

  if (!detail) {
    return (
      <AdminShell activeKey="apps" title="App request">
        <div className="apps-detail">
          <button type="button" className="btn btn-secondary btn-small apps-back" onClick={() => navigate(APPS_LIST_HASH)}>
            ← Back to Apps
          </button>
          <div className="apps-banner apps-banner--error" role="alert">
            {error || 'App not found.'}
          </div>
        </div>
      </AdminShell>
    );
  }

  const isReview = detail.status === 'pending';
  const canProvision = detail.status === 'approved' || detail.status === 'provisioning';
  const canEditTools = PRE_PUBLISH.has(detail.status);
  const secretCount = (detail.tools || []).reduce((n, t) => n + t.secrets.length, 0);
  const collaborators = detail.collaborators || [];
  const tools = detail.tools || [];

  const toolsHeading = isReview
    ? 'Requested tools'
    : canProvision
      ? 'Provision references'
      : 'Tools';

  return (
    <AdminShell activeKey="apps" title={detail.name} subtitle="App request detail">
      <div className="apps-detail">
        <button
          type="button"
          className="btn btn-secondary btn-small apps-back"
          onClick={() => navigate(APPS_LIST_HASH)}
        >
          ← Back to Apps
        </button>

        <header className="apps-detail-header">
          <div className="apps-detail-headrow">
            <h1 className="apps-detail-title">{detail.name}</h1>
            <StatusPill status={detail.status} />
            {detail.published_tool_id && (
              <span className="apps-published-tag">Published</span>
            )}
          </div>
          {detail.description && <p className="apps-detail-desc">{detail.description}</p>}
          <p className="apps-detail-meta">
            Requested by <strong>{detail.requester_name}</strong> ({detail.requester_email})
            {detail.created_at && (
              <> • {formatDateTime(detail.created_at)}</>
            )}
          </p>
          <StatusTracker status={detail.status} />
        </header>

        {detail.status === 'rejected' && detail.admin_notes && (
          <div className="apps-banner apps-banner--error" role="status">
            <strong>Rejection notes:</strong> {detail.admin_notes}
          </div>
        )}

        {message && (
          <div
            role="status"
            className={`apps-banner apps-banner--${message.kind === 'success' ? 'success' : 'error'}`}
          >
            {message.text}
          </div>
        )}

        <section className="apps-section">
          <div className="apps-section-head">
            <h2 className="apps-section-title">Team</h2>
            {canEditTools && can('apps.review') && !addingCollab && (
              <button
                type="button"
                className="ba-btn ba-btn--ghost"
                onClick={() => { setAddingCollab(true); setCollabQuery(''); setCollabResults([]); }}
              >
                Add collaborator
              </button>
            )}
          </div>
          {canEditTools && can('apps.review') && addingCollab && (
            <div className="ba-picker">
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or email…"
                value={collabQuery}
                onChange={(e) => setCollabQuery(e.target.value)}
                aria-label="Search collaborators"
              />
              <div className="ba-collab-results">
                {collabSearching && <p className="ba-collab-empty">Searching…</p>}
                {!collabSearching && collabResults.length === 0 && collabQuery.trim() !== '' && (
                  <p className="ba-collab-empty">No employees match.</p>
                )}
                {!collabSearching && collabResults.map((u) => (
                  <button key={u.id} type="button" className="ba-collab-option" onClick={() => addCollaborator(u)}>
                    {u.photo_url
                      ? <img className="ba-chip-avatar" src={getImageUrl(u.photo_url)} alt="" />
                      : <span className="ba-chip-avatar ba-chip-avatar--fallback" aria-hidden="true">{(u.name || '?').charAt(0).toUpperCase()}</span>}
                    <span className="ba-collab-result-text">
                      <span className="ba-collab-result-name">{u.name}</span>
                      <span className="ba-collab-result-email">{u.email}</span>
                    </span>
                    <span className="ba-collab-add" aria-hidden="true">+</span>
                  </button>
                ))}
              </div>
              <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: 8 }}>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => { setAddingCollab(false); setCollabQuery(''); setCollabResults([]); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {collaborators.length === 0 ? (
            <p className="at-muted">No collaborators.</p>
          ) : (
            <ul className="apps-collab-chips">
              {collaborators.map((c) => (
                <li key={c.user_id} className="apps-collab-chip">
                  {c.photo_url
                    ? <img className="apps-collab-avatar" src={getImageUrl(c.photo_url)} alt="" />
                    : <span className="apps-collab-avatar apps-collab-avatar--fallback" aria-hidden="true">{(c.name || '?').charAt(0).toUpperCase()}</span>}
                  <span>{c.name}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {isReview && can('apps.review') && (
          <section className="apps-section">
            <h2 className="apps-section-title">Review this request</h2>
            <p className="at-muted apps-section-hint">
              Approve to move this app into provisioning, or reject with a reason shown to the requester.
            </p>
            <div className="form-group">
              <label htmlFor="app-review-notes">Notes / reason (shown to requester on reject)</label>
              <textarea
                id="app-review-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                maxLength={2000}
                className="apps-notes"
              />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={() => decide('rejected')} disabled={deciding}>
                {deciding ? 'Working…' : 'Reject'}
              </button>
              <button type="button" className="btn btn-primary" onClick={() => decide('approved')} disabled={deciding}>
                {deciding ? 'Working…' : 'Approve'}
              </button>
            </div>
          </section>
        )}

        <section className="apps-section">
          <div className="apps-section-head">
            <h2 className="apps-section-title">{toolsHeading}</h2>
            {canEditTools && can('apps.review') && !addingTool && (
              <button type="button" className="ba-btn ba-btn--ghost" onClick={openAddTool}>
                Add tool
              </button>
            )}
          </div>
          {canEditTools && can('apps.review') && addingTool && (
            <div className="ba-add-tool-row">
              <div className="ba-add-tool-toggle" role="group" aria-label="Tool type">
                <button
                  type="button"
                  className={`ba-btn ba-btn--ghost${addMode === 'catalog' ? ' ba-btn--active' : ''}`}
                  aria-pressed={addMode === 'catalog'}
                  onClick={() => setAddMode('catalog')}
                >
                  Catalog tool
                </button>
                <button
                  type="button"
                  className={`ba-btn ba-btn--ghost${addMode === 'custom' ? ' ba-btn--active' : ''}`}
                  aria-pressed={addMode === 'custom'}
                  onClick={() => setAddMode('custom')}
                >
                  Custom tool
                </button>
              </div>
              {addMode === 'catalog' ? (
                <div className="form-group">
                  <label htmlFor="adm-add-tool-select">Catalog tool</label>
                  <select id="adm-add-tool-select" value={selTool} onChange={(e) => setSelTool(e.target.value)}>
                    <option value="">— pick a tool —</option>
                    {catalog.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="adm-add-tool-custom">Tool name</label>
                  <input
                    id="adm-add-tool-custom"
                    type="text"
                    value={customName}
                    onChange={(e) => setCustomName(e.target.value)}
                    placeholder="e.g. Internal CRM"
                  />
                </div>
              )}
              <div className="form-group">
                <label htmlFor="adm-add-tool-note">How it&apos;s used (optional)</label>
                <textarea
                  id="adm-add-tool-note"
                  rows={2}
                  value={toolNote}
                  onChange={(e) => setToolNote(e.target.value)}
                  placeholder="Describe how the app will use this tool."
                />
              </div>
              <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="button" className="btn btn-primary btn-small" onClick={addTool} disabled={addingToolBusy}>
                  {addingToolBusy ? 'Adding…' : 'Add'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary btn-small"
                  onClick={() => { setAddingTool(false); setCustomName(''); setToolNote(''); setSelTool(''); setAddMode('catalog'); }}
                  disabled={addingToolBusy}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {canProvision && (
            <p className="at-muted apps-section-hint">
              References point to AWS Parameter Store names. Use the eye to reveal a value on demand.
            </p>
          )}
          {secretCount > 0 && (
            <p className="at-muted apps-section-hint apps-reveal-disclaimer">
              Values are shown transiently and never stored. Screenshots are discouraged and watermarked.
            </p>
          )}
          <div className="tool-grid">
            {tools.map((t) => {
              const ssm = t.secrets.filter((s) => s.assignment_kind === 'ssm');
              const wh = t.secrets.filter((s) => s.assignment_kind === 'warehouse');
              const isCustom = t.is_custom === true || (t.tool_id == null && !t.tool_type);
              const isSso = isCustom && (t.tool_name || '').toLowerCase() === SSO_TOOL_NAME.toLowerCase();
              const type = t.tool_type || 'tool';
              const needsSsoSecret = isSso && !isReview && t.secrets.length === 0;
              return (
                <div className={`tool-card${isSso ? ' tool-card--sso' : ''}${assignTo === t.app_request_tool_id ? ' is-assigning' : ''}`} key={t.app_request_tool_id}>
                  <div className="tool-card-header">
                    <div className="tool-card-icon">
                      {isSso
                        ? <img src={getImageUrl(SSO_TOOL_ICON)} alt="" style={{ width: 26, height: 26, objectFit: 'contain' }} />
                        : !isCustom && t.tool_icon_url
                          ? <img src={getImageUrl(t.tool_icon_url)} alt={t.tool_name} style={{ width: 28, height: 28, objectFit: 'contain' }} />
                          : <span aria-hidden="true">{(t.tool_name || '?').charAt(0).toUpperCase()}</span>}
                    </div>
                    <div className="tool-card-info">
                      <h4 className="tool-card-title">{t.tool_name}</h4>
                      <div className="tool-card-meta">
                        {isSso ? (
                          <span className="tool-type-badge tool-type-badge--sso">Required · per-app secret</span>
                        ) : isCustom ? (
                          <span className="tool-type-badge tool-type-badge--custom">Custom</span>
                        ) : (
                          <span className={`tool-type-badge tool-type-badge--${type}`}>
                            {TOOL_TYPE_LABEL[type] || 'Tool'}
                          </span>
                        )}
                      </div>
                    </div>
                    {canEditTools && !isSso && (
                      <button
                        type="button"
                        className="ba-btn ba-btn--ghost ba-tool-remove"
                        aria-label={`Remove ${t.tool_name}`}
                        onClick={() => removeTool(t)}
                      >
                        ×
                      </button>
                    )}
                  </div>

                  {isSso && (
                    <p className="apps-sso-admin-note">
                      Every app needs its <strong>own</strong> Google SSO credentials. Assign this app&apos;s
                      dedicated SSM secret below — don&apos;t reuse another app&apos;s.
                    </p>
                  )}

                  {needsSsoSecret && canProvision && (
                    <p className="apps-sso-admin-warn" role="status">
                      ⚠ No SSO secret assigned yet for this app.
                    </p>
                  )}

                  {t.usage_note && !isSso && (
                    <p className="tool-card-desc">
                      <span className="apps-usage-label">How they&apos;ll use it:</span> {t.usage_note}
                    </p>
                  )}

                  {!isReview && (
                    <div className="apps-refs">
                      <RefGroup label="Secrets" items={ssm} canProvision={canProvision && can('apps.secrets.manage')} onRemove={unassign} appId={id} viewerLabel={adminEmail} canReveal={can('apps.secrets.reveal')} />
                      <RefGroup label="Datasets" items={wh} canProvision={canProvision && can('apps.secrets.manage')} onRemove={unassign} appId={id} viewerLabel={adminEmail} canReveal={can('apps.secrets.reveal')} />
                    </div>
                  )}

                  {canProvision && can('apps.secrets.manage') && (
                    assignTo === t.app_request_tool_id ? (
                      <div className="apps-assign-panel">
                        <div className="apps-assign-panel-head">
                          <span className="apps-assign-panel-title">Assign a reference</span>
                          <button
                            type="button"
                            className="apps-assign-close"
                            aria-label="Cancel assigning a reference"
                            onClick={() => setAssignTo(null)}
                          >
                            ×
                          </button>
                        </div>

                        <div className="apps-assign-body">
                          <div className="apps-assign-col">
                            <span className="apps-field-label" id={`assign-kind-${t.app_request_tool_id}`}>
                              Reference type
                            </span>
                            <div
                              className="apps-segmented"
                              role="radiogroup"
                              aria-labelledby={`assign-kind-${t.app_request_tool_id}`}
                            >
                              <button
                                type="button"
                                role="radio"
                                aria-checked={kind === 'ssm'}
                                className={`apps-segment ${kind === 'ssm' ? 'is-active' : ''}`}
                                onClick={() => setKind('ssm')}
                              >
                                Secret
                                <span className="apps-segment-sub">revealable value</span>
                              </button>
                              <button
                                type="button"
                                role="radio"
                                aria-checked={kind === 'warehouse'}
                                className={`apps-segment ${kind === 'warehouse' ? 'is-active' : ''}`}
                                onClick={() => setKind('warehouse')}
                              >
                                Dataset
                                <span className="apps-segment-sub">warehouse connection</span>
                              </button>
                            </div>
                          </div>

                          <div className="apps-assign-col">
                            <span className="apps-field-label">Parameter Store name</span>
                            <SsmParamPicker onPick={(name) => assign(t.app_request_tool_id, name)} />
                          </div>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-small btn-primary apps-assign-btn"
                        onClick={() => { setAssignTo(t.app_request_tool_id); setKind('ssm'); }}
                      >
                        + Assign reference
                      </button>
                    )
                  )}
                </div>
              );
            })}
          </div>
        </section>

        {detail.status === 'provisioning' && can('apps.review') && (
          <section className="apps-section apps-section--actions">
            <button type="button" className="btn btn-primary" disabled={secretCount < 1} onClick={activate}>
              Activate app
            </button>
            {secretCount < 1 && (
              <p className="at-muted" style={{ marginTop: 8, fontSize: 12 }}>
                Assign at least one reference to activate.
              </p>
            )}
          </section>
        )}

        {detail.status === 'published' && detail.published_tool_id && (
          <section className="apps-section">
            <h2 className="apps-section-title">Published</h2>
            <p>
              Published to the tools catalog as <strong>{detail.published_tool_name || 'a tool'}</strong>
              {detail.published_at && <> on {formatDateTime(detail.published_at)}</>}.
            </p>
            <p className="at-muted apps-section-hint">
              It&apos;s now a normal catalog tool — add it to departments via the Tools / Departments admin.
            </p>
          </section>
        )}

        {needsPublishForm && (can('apps.publish') || can('apps.review')) && (
          <section className="apps-section">
            <h2 className="apps-section-title">Review publish request</h2>
            <p className="at-muted apps-section-hint">
              The requester submitted this app for publishing
              {detail.publish_requested_at && <> on {formatDateTime(detail.publish_requested_at)}</>}.
              Publish it to the catalog, or request changes with feedback (sent back to the requester as a comment).
            </p>

            {(detail.production_url || detail.production_description) && (
              <div className="apps-prod-summary">
                {detail.production_url && (
                  <p><strong>Production URL:</strong> <a href={detail.production_url} target="_blank" rel="noopener noreferrer">{detail.production_url}</a></p>
                )}
                {detail.production_description && (
                  <p><strong>Production description:</strong> {detail.production_description}</p>
                )}
              </div>
            )}

            {can('apps.publish') && (
              <>
                <h3 className="apps-subhead">Publish to catalog</h3>
                <p className="at-muted apps-section-hint">
                  Link &amp; description default to the requester&apos;s production details — override below if needed.
                </p>
                <div className="form-group">
                  <label htmlFor="pub-type">Type</label>
                  <select id="pub-type" value={pubType} onChange={(e) => setPubType(e.target.value)}>
                    <option value="service">Service</option>
                    <option value="tool">Tool</option>
                    <option value="dashboard">Dashboard</option>
                    <option value="document">Document</option>
                    <option value="sheet">Sheet</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pub-link">Link</label>
                  <input
                    id="pub-link"
                    type="text"
                    value={pubLink}
                    onChange={(e) => setPubLink(e.target.value)}
                    placeholder="https://…"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="pub-category">Category (optional)</label>
                  <select id="pub-category" value={pubCategory} onChange={(e) => setPubCategory(e.target.value)}>
                    <option value="">— None —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="pub-icon">Icon URL (optional)</label>
                  <input
                    id="pub-icon"
                    type="text"
                    value={pubIcon}
                    onChange={(e) => setPubIcon(e.target.value)}
                    placeholder="https://…/icon.png"
                  />
                </div>
                <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                  <button type="button" className="btn btn-primary" onClick={publish} disabled={publishing}>
                    {publishing ? 'Publishing…' : 'Publish to catalog'}
                  </button>
                </div>
              </>
            )}

            {can('apps.publish') && can('apps.review') && <hr className="apps-divider" />}

            {can('apps.review') && (
              <>
                <h3 className="apps-subhead">Or request changes</h3>
                <div className="form-group">
                  <label htmlFor="changes-comment">Feedback for the requester</label>
                  <textarea
                    id="changes-comment"
                    rows={3}
                    maxLength={2000}
                    value={changesComment}
                    onChange={(e) => setChangesComment(e.target.value)}
                    className="apps-notes"
                    placeholder="What needs to change before this can be published?"
                  />
                </div>
                <div className="form-actions" style={{ justifyContent: 'flex-start' }}>
                  <button type="button" className="btn btn-secondary" onClick={requestChanges} disabled={requestingChanges}>
                    {requestingChanges ? 'Sending…' : 'Request changes'}
                  </button>
                </div>
              </>
            )}
          </section>
        )}

        {!isReview && (
          <section className="apps-section">
            <h2 className="apps-section-title">Discussion</h2>
            <CommentThread
              comments={comments}
              body={commentBody}
              setBody={setCommentBody}
              onPost={postComment}
              posting={posting}
              canPost={can('apps.review')}
            />
          </section>
        )}

        {!isReview && (
          <section className="apps-section">
            <div className="apps-reveal-history-head">
              <h2 className="apps-section-title" style={{ margin: 0 }}>Secret reveal history</h2>
              {reveals.length > 0 && <span className="apps-reveal-count">{reveals.length}</span>}
            </div>
            <p className="at-muted apps-section-hint">
              Every on-demand reveal of a parameter value is recorded here (parameter name + who + when —
              never the value). Reveals don&apos;t appear in the global activity log.
            </p>
            {reveals.length === 0 ? (
              <p className="at-muted">No reveals recorded for this app yet.</p>
            ) : (
              <ul className="apps-reveal-history">
                {reveals.map((r) => (
                  <li key={r.id} className="apps-reveal-history-row">
                    <span className="apps-reveal-history-dot" aria-hidden="true" />
                    <div className="apps-reveal-history-body">
                      <span className="apps-reveal-history-detail">{r.details}</span>
                      <span className="apps-reveal-history-meta">
                        {r.user_name || r.user_email || 'Unknown'}
                        {r.created_at && <> · {formatDateTime(r.created_at)}</>}
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        )}
      </div>
    </AdminShell>
  );
}

function RefGroup({ label, items, canProvision, onRemove, appId, viewerLabel, canReveal }) {
  return (
    <div className="apps-ref-group">
      <strong className="apps-ref-label">{label}</strong>
      <ul className="apps-ref-list">
        {items.map((s) => (
          canReveal ? (
            <SecretRevealRow
              key={s.id}
              secret={s}
              appId={appId}
              viewerLabel={viewerLabel}
              canReveal
              canRemove={canProvision}
              onRemove={onRemove}
            />
          ) : (
            <li key={s.id} className="apps-ref-list-item">
              <code className="apps-secret-name">{s.ssm_param_name}</code>
            </li>
          )
        ))}
        {items.length === 0 && <li className="at-muted apps-ref-empty">none</li>}
      </ul>
    </div>
  );
}

